/**
 * OurStancePanel — Home landing panel that teases the FBC stance,
 * with a CTA into the full /about page.
 */

import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Quote } from 'lucide-react';

export const OurStancePanel = ({ onReadMore }) => {
  return (
    <section className="py-16 border-y bg-muted/20" data-testid="home-our-stance">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
              Our Stance
            </p>
            <h2
              className="text-2xl md:text-3xl font-semibold mb-5 leading-tight"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              The honest picture
            </h2>

            <div
              className="space-y-3 text-base md:text-lg leading-relaxed text-foreground/85"
              style={{ fontFamily: 'Georgia, serif' }}
            >
              <p>
                Real estate development is a hard business. Developers take real
                risks — and earn profits and the goodwill of their name on a
                well-executed project.
              </p>
              <p>
                It is equally true that the <span className="font-semibold">buyer</span>{' '}
                pays costs <em>plus</em> profits to the developer, and is the real
                owner of the project — the developer is the custodial owner until
                completion and handover.
              </p>
              <p>
                Buyers have no control over developer decisions, yet bear every
                consequence of delay, design flaw, quality compromise, or defect.
              </p>
            </div>

            <blockquote
              className="mt-6 border-l-4 border-primary pl-5 py-2 bg-primary/5 rounded-r-sm"
              data-testid="home-stance-pullquote"
            >
              <Quote className="h-4 w-4 text-primary/60 mb-1" />
              <p
                className="text-lg md:text-xl font-semibold leading-snug"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Buyers Led. Buyers First.
                <br />
                Clear-eyed about both sides.
              </p>
            </blockquote>

            <div className="mt-6">
              <Button
                variant="ghost"
                onClick={onReadMore}
                data-testid="home-stance-readmore"
                className="text-primary hover:bg-primary/10"
              >
                Read our full stance <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
